import React from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, Target, Clock, Flame, Home, RotateCcw } from 'lucide-react';

interface ResultsData {
  score: number;
  accuracy: number;
  totalTime: number;
  correctCount: number;
  incorrectCount: number;
  longestStreak: number;
  mode: 'training' | 'challenge';
}

const ResultsContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.xl};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const ResultsCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  max-width: 600px;
  width: 100%;

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: 2.5rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: 767px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.1rem;
`;

const ScoreSection = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.lg};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const ScoreLabel = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  opacity: 0.9;
`;

const ScoreValue = styled.div`
  font-size: 4rem;
  font-weight: 800;
  line-height: 1;

  @media (max-width: 767px) {
    font-size: 3rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div<{ $color?: string }>`
  background: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border-left: 4px solid ${({ $color, theme }) => $color || theme.colors.accent};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const StatIcon = styled.div<{ $color?: string }>`
  color: ${({ $color, theme }) => $color || theme.colors.accent};
  flex-shrink: 0;
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatLabel = styled.div`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: center;

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const Button = styled.button<{ $primary?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  min-height: 52px;
  min-width: 180px;
  background: ${({ $primary, theme }) =>
    $primary
      ? `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary})`
      : theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  border: ${({ $primary, theme }) => ($primary ? 'none' : `2px solid ${theme.colors.primary}`)};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const ResultsScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const results = (location.state as { results?: ResultsData })?.results;

  // If no results data, redirect after delay
  React.useEffect(() => {
    if (!results) {
      const timer = setTimeout(() => {
        navigate('/modes');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [results, navigate]);

  // If no results data, show error message
  if (!results) {
    return (
      <ResultsContainer>
        <ResultsCard
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Header>
            <Title>No Results Data</Title>
            <Subtitle>Redirecting to modes...</Subtitle>
          </Header>
        </ResultsCard>
      </ResultsContainer>
    );
  }

  const formatTime = (milliseconds: number) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${seconds}s`;
  };

  const handlePlayAgain = () => {
    if (results.mode === 'training') {
      navigate('/game/training');
    } else {
      navigate('/game');
    }
  };

  const handleHome = () => {
    navigate('/modes');
  };

  return (
    <ResultsContainer>
      <ResultsCard
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      >
        <Header>
          <Title>
            <Trophy size={40} />
            Game Complete!
          </Title>
          <Subtitle>
            {results.mode === 'training' ? 'Training Session' : 'Challenge Mode'}
          </Subtitle>
        </Header>

        <ScoreSection>
          <ScoreLabel>Final Score</ScoreLabel>
          <ScoreValue>{results.score.toLocaleString()}</ScoreValue>
        </ScoreSection>

        <StatsGrid>
          <StatCard $color="#4CAF50">
            <StatIcon $color="#4CAF50">
              <Target size={32} />
            </StatIcon>
            <StatContent>
              <StatLabel>Accuracy</StatLabel>
              <StatValue>{results.accuracy}%</StatValue>
            </StatContent>
          </StatCard>

          <StatCard $color="#2196F3">
            <StatIcon $color="#2196F3">
              <Clock size={32} />
            </StatIcon>
            <StatContent>
              <StatLabel>Time</StatLabel>
              <StatValue>{formatTime(results.totalTime)}</StatValue>
            </StatContent>
          </StatCard>

          <StatCard $color="#FF9800">
            <StatIcon $color="#FF9800">
              <Trophy size={32} />
            </StatIcon>
            <StatContent>
              <StatLabel>Correct</StatLabel>
              <StatValue>
                {results.correctCount}/{results.correctCount + results.incorrectCount}
              </StatValue>
            </StatContent>
          </StatCard>

          <StatCard $color="#9C27B0">
            <StatIcon $color="#9C27B0">
              <Flame size={32} />
            </StatIcon>
            <StatContent>
              <StatLabel>Best Streak</StatLabel>
              <StatValue>{results.longestStreak}</StatValue>
            </StatContent>
          </StatCard>
        </StatsGrid>

        <ButtonGroup>
          <Button $primary onClick={handlePlayAgain}>
            <RotateCcw size={20} />
            Play Again
          </Button>
          <Button onClick={handleHome}>
            <Home size={20} />
            Back to Modes
          </Button>
        </ButtonGroup>
      </ResultsCard>
    </ResultsContainer>
  );
};

export default ResultsScreen;
